import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import { type FormEvent, type ReactNode, useEffect, useMemo, useState } from 'react'
import { api, type TimeEntry } from '../lib/api'
import { invalidateEntryData } from '../lib/invalidate'
import { notify } from '../lib/notify'
import { resolveSuggestionFill } from '../lib/suggestion-fill'
import { useIsMobile } from '../lib/use-media-query'
import { DescriptionAutocomplete } from './description-autocomplete'
import { TagPicker } from './tag-picker'
import { Button } from './ui/button'
import { confirm } from './ui/confirm-dialog'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Select } from './ui/select'
import { SidePanel } from './ui/side-panel'

export interface EntryDraft {
  startTime: Date
  endTime: Date
}

interface EntryFormDialogProps {
  open: boolean
  onClose: () => void
  entry?: TimeEntry | null
  defaultDate?: string
  draft?: EntryDraft | null
  /** Admin/leader: create new entries for this user instead of self. */
  forUserId?: string | null
}

function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function localInputToIso(local: string): string {
  return new Date(local).toISOString()
}

export interface EntryFormBodyResult {
  title: string
  description: string
  body: ReactNode
  footer: ReactNode
  isDirty: boolean
}

interface UseEntryFormArgs {
  open: boolean
  entry?: TimeEntry | null
  defaultDate?: string
  draft?: EntryDraft | null
  onClose: () => void
  /** Admin/leader edit-mode: create new entries for this user instead of self.
   * Only affects creation — updates/deletes already target the entry's owner. */
  forUserId?: string | null
}

/** Headless hook returning the entry-form fields + footer actions. Used by
 * both the standalone `EntryFormDialog` (legacy wrapper) and the unified
 * `ErfassenPanel`. */
export function useEntryForm({
  open,
  entry,
  defaultDate,
  draft,
  onClose,
  forUserId,
}: UseEntryFormArgs): EntryFormBodyResult {
  const queryClient = useQueryClient()
  const isEdit = !!entry
  // Kein Autofokus am Handy: sonst reißt der Öffnen-Tap sofort die Tastatur
  // hoch, bevor der Nutzer etwas sieht. Am Desktop weiterhin bequem direkt
  // ins Beschreibungsfeld tippen.
  const isMobile = useIsMobile()

  const { data: customersData } = useQuery({
    queryKey: ['customers', { archived: false }],
    queryFn: () => api.listCustomers(),
    enabled: open,
  })
  const { data: projectsData } = useQuery({
    queryKey: ['projects', { archived: false }],
    queryFn: () => api.listProjects(),
    enabled: open,
  })
  const { data: servicesData } = useQuery({
    queryKey: ['services', { archived: false }],
    queryFn: () => api.listServices(),
    enabled: open,
  })
  const { data: tagsData } = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.listTags(),
    enabled: open,
  })
  const customers = customersData?.customers ?? []
  const projects = projectsData?.projects ?? []
  const servicesList = servicesData?.services ?? []
  const tags = tagsData?.tags ?? []

  const [description, setDescription] = useState('')
  const [projectId, setProjectId] = useState('')
  const [serviceId, setServiceId] = useState('')
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [travelTo, setTravelTo] = useState('')
  const [travelCountsAsWork, setTravelCountsAsWork] = useState(false)
  const [travelFrom, setTravelFrom] = useState('')
  const [billable, setBillable] = useState(true)
  const [notes, setNotes] = useState('')
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  // Track whether the user actually touched any field this session — drives
  // the "Daten verworfen" toast when switching tabs in ErfassenPanel.
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (!open) return
    if (entry) {
      setDescription(entry.description)
      setProjectId(entry.projectId ?? '')
      setServiceId(entry.serviceId ?? '')
      setStartTime(isoToLocalInput(entry.startTime))
      setEndTime(isoToLocalInput(entry.endTime))
      setTravelTo(entry.travelTimeToMinutes > 0 ? String(entry.travelTimeToMinutes) : '')
      setTravelFrom(entry.travelTimeFromMinutes > 0 ? String(entry.travelTimeFromMinutes) : '')
      setTravelCountsAsWork(entry.travelCountsAsWork)
      setBillable(entry.billable)
      setNotes(entry.notes ?? '')
      setSelectedTagIds(entry.tagIds ?? [])
    } else {
      // Last-Used-Prefill: Mitarbeiter mit Reisezeit-Pflicht haken einmal an,
      // danach ist die Checkbox vorbelegt (localStorage, pro Browser).
      try {
        setTravelCountsAsWork(window.localStorage.getItem('entry:travel-counts-as-work') === '1')
      } catch {
        setTravelCountsAsWork(false)
      }
      setDescription('')
      setProjectId('')
      setServiceId('')
      if (draft) {
        setStartTime(isoToLocalInput(draft.startTime.toISOString()))
        setEndTime(isoToLocalInput(draft.endTime.toISOString()))
      } else {
        const base = defaultDate ? new Date(defaultDate) : new Date()
        base.setHours(9, 0, 0, 0)
        const startDefault = isoToLocalInput(base.toISOString())
        base.setHours(10, 0, 0, 0)
        const endDefault = isoToLocalInput(base.toISOString())
        setStartTime(startDefault)
        setEndTime(endDefault)
      }
      setTravelTo('')
      setTravelFrom('')
      setBillable(true)
      setNotes('')
      setSelectedTagIds([])
    }
    setDirty(false)
  }, [entry, open, defaultDate, draft])

  const markDirty = () => setDirty(true)

  const create = useMutation({
    mutationFn: () =>
      api.createEntry({
        description,
        startTime: localInputToIso(startTime),
        endTime: endTime ? localInputToIso(endTime) : null,
        projectId: projectId || null,
        serviceId: serviceId || null,
        billable,
        notes: notes || null,
        tagIds: selectedTagIds,
        travelTimeToMinutes: Number(travelTo) || 0,
        travelTimeFromMinutes: Number(travelFrom) || 0,
        travelCountsAsWork,
        userId: forUserId ?? undefined,
      }),
    onSuccess: () => {
      invalidateEntryData(queryClient)
      notify.success('Eintrag angelegt')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const update = useMutation({
    mutationFn: () =>
      api.updateEntry(entry?.id ?? '', {
        description,
        startTime: localInputToIso(startTime),
        endTime: endTime ? localInputToIso(endTime) : null,
        projectId: projectId || null,
        serviceId: serviceId || null,
        billable,
        notes: notes || null,
        tagIds: selectedTagIds,
        travelTimeToMinutes: Number(travelTo) || 0,
        travelTimeFromMinutes: Number(travelFrom) || 0,
        travelCountsAsWork,
      }),
    onSuccess: () => {
      invalidateEntryData(queryClient)
      notify.success('Eintrag aktualisiert')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!description.trim()) {
      notify.error('Beschreibung ist Pflicht')
      return
    }
    if (!startTime) {
      notify.error('Startzeit ist Pflicht')
      return
    }
    if (endTime && new Date(endTime) <= new Date(startTime)) {
      notify.error('Endzeit muss nach Startzeit liegen')
      return
    }
    if (isEdit) update.mutate()
    else create.mutate()
  }

  const remove = useMutation({
    mutationFn: () => api.deleteEntry(entry?.id ?? ''),
    onSuccess: () => {
      invalidateEntryData(queryClient)
      notify.success('Eintrag gelöscht')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onDelete = async () => {
    const ok = await confirm({
      title: 'Eintrag löschen?',
      description: entry?.description,
      variant: 'danger',
      confirmLabel: 'Löschen',
    })
    if (ok) remove.mutate()
  }

  const customerMap = useMemo(() => new Map(customers.map((c) => [c.id, c])), [customers])
  const isPending = create.isPending || update.isPending || remove.isPending

  const body = (
    <form onSubmit={onSubmit} className="space-y-4 pb-2">
      <div className="space-y-2">
        <Label htmlFor="ef-description">Beschreibung</Label>
        <DescriptionAutocomplete
          id="ef-description"
          value={description}
          onChange={(next) => {
            setDescription(next)
            markDirty()
          }}
          onPick={(s) => {
            setDescription(s.description)
            // Projekt/Tätigkeit der letzten Verwendung NUR in noch leere
            // Felder übernehmen — eine manuelle Auswahl bleibt unangetastet.
            const fill = resolveSuggestionFill(s, { projectId, serviceId }, projects, servicesList)
            if (fill.projectId) setProjectId(fill.projectId)
            if (fill.serviceId) setServiceId(fill.serviceId)
            markDirty()
          }}
          projects={projects}
          services={servicesList}
          placeholder="z.B. Code-Review Sprint 14"
          autoFocus={!isMobile}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="ef-start">Start</Label>
          <Input
            id="ef-start"
            type="datetime-local"
            value={startTime}
            onChange={(e) => {
              setStartTime(e.target.value)
              markDirty()
            }}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ef-end">Ende</Label>
          <Input
            id="ef-end"
            type="datetime-local"
            value={endTime}
            onChange={(e) => {
              setEndTime(e.target.value)
              markDirty()
            }}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ef-travel-to">Reisezeit Anfahrt (Min.)</Label>
          <Input
            id="ef-travel-to"
            type="number"
            min="0"
            step="5"
            value={travelTo}
            onChange={(e) => {
              setTravelTo(e.target.value)
              markDirty()
            }}
            placeholder="0"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="ef-travel-from">Reisezeit Abfahrt (Min.)</Label>
          <Input
            id="ef-travel-from"
            type="number"
            min="0"
            step="5"
            value={travelFrom}
            onChange={(e) => {
              setTravelFrom(e.target.value)
              markDirty()
            }}
            placeholder="0"
          />
        </div>
      </div>
      {(Number(travelTo) > 0 || Number(travelFrom) > 0) && (
        <div className="space-y-2">
          <label className="flex items-start gap-2.5 rounded-lg border border-border-subtle bg-bg-overlay/40 p-3 cursor-pointer">
            <input
              type="checkbox"
              checked={travelCountsAsWork}
              onChange={(e) => {
                setTravelCountsAsWork(e.target.checked)
                try {
                  window.localStorage.setItem(
                    'entry:travel-counts-as-work',
                    e.target.checked ? '1' : '0',
                  )
                } catch {
                  // localStorage gesperrt — Prefill entfällt dann eben.
                }
                markDirty()
              }}
              className="mt-0.5 h-4 w-4 accent-[var(--accent,#e8590c)]"
            />
            <span className="text-sm">
              <span className="text-text-primary">Reisezeit zählt als Arbeitszeit</span>
              <span className="block text-xs text-text-tertiary">
                Angehakt fließt die Reisezeit in Ist-Zeit, Stundenkonto und Reports ein — ohne Haken
                ist sie nur informativ (private Reisezeit).
              </span>
            </span>
          </label>
          <p className="text-xs text-text-tertiary">
            Reisezeit rahmt den Eintrag im Kalender ein: Anfahrt vor Start, Abfahrt nach Ende.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="ef-project">Projekt</Label>
          <Select
            id="ef-project"
            value={projectId}
            onChange={(e) => {
              setProjectId(e.target.value)
              markDirty()
            }}
          >
            <option value="">— ohne —</option>
            {projects.map((p) => {
              const cust = customerMap.get(p.customerId)
              return (
                <option key={p.id} value={p.id}>
                  {cust ? `${cust.name} – ` : ''}
                  {p.name}
                </option>
              )
            })}
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="ef-service">Tätigkeit</Label>
          <Select
            id="ef-service"
            value={serviceId}
            onChange={(e) => {
              setServiceId(e.target.value)
              markDirty()
            }}
          >
            <option value="">— ohne —</option>
            {servicesList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Tags</Label>
        <TagPicker
          tags={tags}
          selectedIds={selectedTagIds}
          onToggle={(id) => {
            setSelectedTagIds((prev) =>
              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
            )
            markDirty()
          }}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="ef-notes">Notizen (optional)</Label>
        <textarea
          id="ef-notes"
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value)
            markDirty()
          }}
          rows={3}
          className="flex w-full rounded-md border border-border-default bg-bg-overlay px-3 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30"
          placeholder="Längere Notizen, Verlinkungen, …"
        />
      </div>

      {/* Abrechenbar — lebt bewusst im scrollbaren Bereich über dem Footer.
          Auf Touch ist das gesamte Label das Tap-Ziel (≥44px Hit-Area),
          Desktop-Optik bleibt unverändert (nur touch:-Klassen). */}
      <label className="flex items-center gap-2 text-sm cursor-pointer touch:min-h-11 touch:-mx-2 touch:px-2 touch:rounded-md">
        <input
          type="checkbox"
          checked={billable}
          onChange={(e) => {
            setBillable(e.target.checked)
            markDirty()
          }}
          className="h-4 w-4 touch:h-5 touch:w-5 rounded border-border-default bg-bg-overlay accent-accent"
        />
        <span>Abrechenbar</span>
      </label>
    </form>
  )

  const footer = (
    <>
      {isEdit && (
        <Button
          size="sm"
          variant="ghost"
          onClick={onDelete}
          type="button"
          disabled={isPending}
          className="mr-auto text-danger hover:bg-danger/10 hover:text-danger"
        >
          <Trash2 className="h-3.5 w-3.5" /> Löschen
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={onClose} type="button">
        Abbrechen
      </Button>
      <Button size="sm" onClick={onSubmit} type="button" disabled={isPending}>
        {isEdit ? 'Speichern' : 'Anlegen'}
      </Button>
    </>
  )

  return {
    // Einheitliches Titelmuster über die Erfassen-Tabs: „Neuer/Neue X" (C5)
    title: isEdit ? 'Eintrag bearbeiten' : 'Neuer Eintrag',
    description: isEdit
      ? 'Zeit, Projekt, Tags und Notizen anpassen.'
      : 'Manueller Eintrag mit Datum + Uhrzeit.',
    body,
    footer,
    isDirty: dirty,
  }
}

/** Render-prop wrapper around `useEntryForm` — used by ErfassenPanel to
 * render just the form fields inside the tab body. Footer + title are
 * exposed for the parent to compose into its SidePanel. */
export function EntryFormBody(props: {
  open: boolean
  entry?: TimeEntry | null
  defaultDate?: string
  draft?: EntryDraft | null
  onClose: () => void
  children: (result: EntryFormBodyResult) => ReactNode
}) {
  const { children, ...rest } = props
  const result = useEntryForm(rest)
  return <>{children(result)}</>
}

export function EntryFormDialog({
  open,
  onClose,
  entry,
  defaultDate,
  draft,
  forUserId,
}: EntryFormDialogProps) {
  const { title, description, body, footer } = useEntryForm({
    open,
    entry,
    defaultDate,
    draft,
    onClose,
    forUserId,
  })
  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      width="sm:w-[480px] lg:w-[560px]"
      footer={footer}
    >
      {body}
    </SidePanel>
  )
}
